import { c as _c } from "react/compiler-runtime";
import i18n from 'i18next';
import { Alert, ButtonGroup, Dialog, Form, FormButton, FormField, useTranslationContext, type FormSubmitHandler } from 'platform/components';
import { Hide, Show, Spinner } from 'platform/foundation';
import { VStack } from 'platform/foundation-tw';
import { head } from 'ramda';
import { suffixTestId, type TestIdProps } from 'shared/web';
import type { AsyncExportDataRequestBody } from '../../../../../../types/api/data';
import { useExport } from '../../hooks/useExport';
import type { ExportOptions } from '../../types/ExportOptions';
type ExportFileType = AsyncExportDataRequestBody['fileType'];
type ExportOptionsLocal = Omit<ExportOptions, 'fileType'> & {
  fileType: ExportFileType[];
};
const FILE_TYPE_OPTIONS = [{
  value: 'excel',
  label: 'Excel'
}, {
  value: 'csv',
  label: 'CSV'
}, {
  value: 'pdf',
  label: 'PDF'
}];
const FORM_DEFAULT_VALUES: ExportOptionsLocal = {
  fileType: ['csv'],
  columnDelimiter: 'comma',
  decimalDelimiter: 'dot'
};
const getDecimalDelimiters = () => [{
  value: 'comma',
  label: i18n.t('page.dataGrid.export.decimalDelimiter.comma')
}, {
  value: 'dot',
  label: i18n.t('page.dataGrid.export.decimalDelimiter.dot')
}];
const getColumnDelimiters = () => [{
  value: 'comma',
  label: i18n.t('page.dataGrid.export.columnDelimiter.comma')
}, {
  value: 'semicolon',
  label: i18n.t('page.dataGrid.export.columnDelimiter.semicolon')
}];
export type ExportDialogProps = {
  isOpen: boolean;
  onClose: () => void;
} & TestIdProps;
export function ExportDialog(props) {
  const $ = _c(21);
  const t = useTranslationContext();
  let t0;
  if ($[0] !== props.onClose) {
    t0 = {
      onClose: props.onClose
    };
    $[0] = props.onClose;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    exportState,
    onExport
  } = useExport(t0);
  let t1;
  if ($[2] !== onExport) {
    t1 = async values => {
      await onExport({
        ...values,
        fileType: head(values.fileType) ?? "csv"
      });
    };
    $[2] = onExport;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onHandleSubmit = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("page.datagrid.export.optionsTitle");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = props.isOpen;
  const t4 = props.onClose;
  let t5;
  if ($[6] !== props) {
    t5 = suffixTestId("exportOptionsDialog", props);
    $[6] = props;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== exportState || $[9] !== props || $[10] !== t) {
    t6 = (control, formApi) => {
      const fileType = head(formApi.watch("fileType"));
      return <VStack spacing={4}><FormField control={control} name="fileType" type="chips" options={FILE_TYPE_OPTIONS} label={t("page.datagrid.export.fileType")} data-testid={suffixTestId("exportOptionsDialog-fileType", props)} /><Hide when={fileType === "pdf"}><FormField control={control} name="decimalDelimiter" isNotClearable={true} menuInPortal={true} type="choice" options={getDecimalDelimiters()} label={t("page.datagrid.export.decimalSeparator")} data-testid={suffixTestId("exportOptionsDialog-decimalSeparator", props)} /><Show when={fileType === "csv"}><FormField control={control} name="columnDelimiter" isNotClearable={true} menuInPortal={true} type="choice" options={getColumnDelimiters()} label={t("page.datagrid.export.columnSeparator")} data-testid={suffixTestId("exportOptionsDialog-columnSeparator", props)} /></Show></Hide><Show when={fileType === "pdf"}><Alert variant="warning" message={t("page.datagrid.export.columnLengthAlert")} /></Show>{exportState === "exporting" && <Spinner variant="overlay" />}{exportState !== "exporting" && <ButtonGroup align="right"><FormButton control={control} type="submit" title={t("page.datagrid.button.export")} data-testid={suffixTestId("exportOptionsDialog-submitButton", props)} /></ButtonGroup>}</VStack>;
    };
    $[8] = exportState;
    $[9] = props;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onHandleSubmit || $[13] !== t6) {
    t7 = <Form onSubmit={onHandleSubmit} defaultValues={FORM_DEFAULT_VALUES}>{t6}</Form>;
    $[12] = onHandleSubmit;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== props.isOpen || $[16] !== props.onClose || $[17] !== t2 || $[18] !== t5 || $[19] !== t7) {
    t8 = <Dialog title={t2} isOpen={t3} onClose={t4} size="small" data-testid={t5}>{t7}</Dialog>;
    $[15] = props.isOpen;
    $[16] = props.onClose;
    $[17] = t2;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}